import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import Conversation from "../../Components/Conversation/Conversation";
import { DashlyTextInput } from "../../Components/inputs";
import PermissionCheck from "../../Components/PermissionCheck";
import { DASHLY_PERMISSIONS } from "../../Util/dashlyPermissions";
import "./KnowledgeBaseAskAi.scss";

const canUseKnowledgeBaseAskAi = (): boolean =>
    PermissionCheck(DASHLY_PERMISSIONS.ASK_AI_KNOWLEDGE_BASE);

interface KnowledgeBaseAskAiProps {
    articleUuid?: string;
    variant?: "list" | "detail";
}

const KnowledgeBaseAskAi: React.FC<KnowledgeBaseAskAiProps> = ({
    articleUuid,
    variant = "list",
}) => {
    const { t } = useTranslation();
    const [draft, setDraft] = useState("");
    const [pendingQuestion, setPendingQuestion] = useState<string>();
    const [conversationVisible, setConversationVisible] = useState(false);
    const allowed = useMemo(() => canUseKnowledgeBaseAskAi(), []);

    if (!allowed) {
        return null;
    }

    const openConversation = (question?: string) => {
        setPendingQuestion(question);
        setConversationVisible(true);
    };

    const submitDraft = () => {
        const question = draft.trim();
        setDraft("");
        openConversation(question || undefined);
    };

    const hideConversation = (value: boolean | ((previous: boolean) => boolean)) => {
        const next = typeof value === "function" ? value(conversationVisible) : value;
        setConversationVisible(next);
        if (!next) {
            setPendingQuestion(undefined);
        }
    };

    return (
        <>
            <section className={`knowledge-base-ask-ai knowledge-base-ask-ai--${variant}`}>
                <div className="knowledge-base-ask-ai__copy">
                    <h3 className="knowledge-base-ask-ai__title">{t("knowledgeBase.askAi.title")}</h3>
                    <p className="knowledge-base-ask-ai__description">
                        {t(variant === "detail"
                            ? "knowledgeBase.askAi.detailDescription"
                            : "knowledgeBase.askAi.description")}
                    </p>
                </div>
                <form
                    className="knowledge-base-ask-ai__compose"
                    onSubmit={(event) => {
                        event.preventDefault();
                        submitDraft();
                    }}
                >
                    <DashlyTextInput
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        placeholder={t("knowledgeBase.askAi.placeholder") as string}
                        aria-label={t("knowledgeBase.askAi.placeholder") as string}
                        leadingIcon="pi pi-sparkles"
                    />
                    <Button
                        type="submit"
                        label={t("knowledgeBase.askAi.open") as string}
                        aria-label={t("knowledgeBase.askAi.open") as string}
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        className="knowledge-base-ask-ai__send"
                    />
                </form>
                <p className="knowledge-base-ask-ai__hint">
                    {t(variant === "detail" ? "knowledgeBase.askAi.detailHint" : "knowledgeBase.askAi.hint")}
                </p>
            </section>
            {conversationVisible &&
                createPortal(
                    <div className="knowledge-base-ask-ai__panel">
                        <div className="relative">
                            <Conversation
                                key={pendingQuestion ?? "browse"}
                                type="ASKAI"
                                fullscreen={false}
                                groupByDate={true}
                                selectionAllowed={true}
                                minMessageSize={0}
                                location="KNOWLEDGE_BASE"
                                locationIdentifier={articleUuid}
                                initialQuestion={pendingQuestion}
                                setVisible={hideConversation}
                            />
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
};

export default KnowledgeBaseAskAi;
